import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Avatar,
  Space,
  Input,
  Modal,
  Switch,
  Progress,
} from "antd";
import { getUsersApi, updateUserStatusApi, getUserMetricsApi } from "../../services/api";
import StatCard from "./StatCard";

const { Search } = Input;

const ROLE_BAR_COLORS = ["#534ab7", "#1E88E5", "#43A047", "#FFA726", "#E24B4A"];

export default function Customers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const fetchUsers = async () => {
    const res = await getUsersApi();
    setUsers(res.data.data || []);
  };

  const fetchMetrics = async () => {
    try {
      const res = await getUserMetricsApi();
      setMetrics(res.data.data || null);
    } catch {
      // Non-fatal — the metrics row just stays hidden.
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMetrics();
  }, []);

  // "Live Now" is the one figure meant to behave like a live number rather
  // than a page-load snapshot, same exception made on Dashboard.jsx.
  useEffect(() => {
    const interval = setInterval(fetchMetrics, 30_000);
    return () => clearInterval(interval);
  }, []);

  const signupTrend = metrics?.signupTrend || [];
  const roleBreakdown = metrics?.roleBreakdown || [];
  const maxTrend = Math.max(...signupTrend.map((d) => d.count || 0), 1);
  const maxRoleCount = Math.max(...roleBreakdown.map((r) => r.count || 0), 1);

  const handleStatusChange = async (id, isActive) => {
    await updateUserStatusApi(id, { isActive });
    fetchUsers();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v, r) => (
        <Space>
          <Avatar style={{ background: "#e1f5ee", color: "#0f6e56" }}>
            {v?.charAt(0)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{v}</div>
            <div style={{ fontSize: 11, color: "#888" }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (v) => v || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v, r) => (
        <Switch
          checked={v}
          onChange={(checked) => handleStatusChange(r.id, checked)}
          style={{ background: v ? "#534ab7" : undefined }}
        />
      ),
    },
    {
      title: "",
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setSelectedUser(r);
              setOpen(true);
            }}
          >
            View Profile
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Customers</div>
          <div className="page-sub">{users.length} registered customers</div>
        </div>

        <Search placeholder="Search customers…" style={{ width: 240 }} />
      </div>

      {/* USER METRICS */}
      {metrics && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
            <StatCard label="TOTAL USERS" value={metrics.totalUsers} positive color="#111" />
            <StatCard label="LIVE NOW" value={metrics.liveNow} positive color="#1E88E5" />
            <StatCard label="NEW TODAY" value={metrics.newToday} positive color="#43A047" />
            <StatCard label="NEW THIS WEEK" value={metrics.newThisWeek} positive color="#43A047" />
            <StatCard label="NEW THIS MONTH" value={metrics.newThisMonth} positive color="#43A047" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 24 }}>
            {/* SIGNUP TREND */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20 }}>
              <div className="section-title">Signups — Last 14 Days</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 160, paddingTop: 8, overflowX: "auto" }}>
                {signupTrend.map((d) => (
                  <div key={d.date} style={{ flex: 1, minWidth: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 10, color: "#888" }}>{d.count}</div>
                    <div
                      style={{
                        width: "100%",
                        background: "#eeedfe",
                        borderRadius: 4,
                        height: `${Math.max(4, Math.round((d.count / maxTrend) * 110))}px`,
                        transition: "height .3s",
                      }}
                    />
                    <div style={{ fontSize: 9, color: "#aaa" }}>{d.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ROLE BREAKDOWN */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20 }}>
              <div className="section-title">By Role</div>
              {roleBreakdown.map((r, i) => (
                <div key={r.role} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: "#888" }}>{r.role}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{r.count}</span>
                  </div>
                  <Progress
                    percent={Math.round((r.count / maxRoleCount) * 100)}
                    showInfo={false}
                    strokeColor={ROLE_BAR_COLORS[i % ROLE_BAR_COLORS.length]}
                    trailColor="rgba(0,0,0,0.06)"
                    size="small"
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        size="small"
      />

      {/* ================= VIEW PROFILE MODAL ================= */}
      <Modal
        open={open}
        title="User Profile"
        footer={null}
        onCancel={() => setOpen(false)}
      >
        {selectedUser && (
          <div style={{ display: "grid", gap: 10 }}>
            <p><b>Name:</b> {selectedUser.name}</p>
            <p><b>Email:</b> {selectedUser.email}</p>
            <p><b>Phone:</b> {selectedUser.phone}</p>
            <p><b>Role:</b> {selectedUser.role?.name || "USER"}</p>

            <p>
              <b>Status:</b>{" "}
              {selectedUser.isActive ? "Active" : "Inactive"}
            </p>

            <Switch
              checked={selectedUser.isActive}
              onChange={(checked) =>
                handleStatusChange(selectedUser.id, checked)
              }
              style={{ background: selectedUser.isActive ? "#534ab7" : undefined }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
